import { Fragment, useEffect, memo, useRef } from "react";
import { initPreviewSize } from "@design/pc/modules/previewSize";
import { mix } from "@spa/utils/assetPathResolver";
import {copyTextToClipboard} from "@spa/utils/clipboardHelper";
import {saveCookie} from "@spa/utils/cookieHelper";
import GoogleMap from "@spa/features/search_job/pc/components/detail/GoogleMap";
import {useJobSwitch} from "@spa/features/search_job/pc/contexts/JobSwitchContext";
import JobDetailError from "@spa/features/search_job/pc/components/detail/JobDetailError";
import {useFocus} from "@spa/features/search_job/pc/contexts/FocusContext";
import {trackJobDetail} from "@spa/features/search_job/pc/api/analytics/trackPage";
import {trackLinkCopy, trackLinkDetailPage, trackShowMore, trackDetailCv} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {useTrack} from "@spa/features/search_job/pc/contexts/TrackContext";
import {usePage} from "@inertiajs/react";
import {initShowMore} from "@design/pc/modules/showMore.js";

const JobDetail = ({
    shouldNormalizeLocation,
    savedJobsForSearchResult,
    handleSaveJob,
    translationsJobItems,
}) => {
    const { translations } = usePage().props;
    const { jobViewModelForDetail, isJobDetailLoading, isFirstRender, activeUniqueId } = useJobSwitch();
    const { channelName } = useTrack();
    const isSaved = savedJobsForSearchResult[activeUniqueId];
    const {focusDetailRef, moveFocusToPanel} = useFocus();
    const isRenderJobDetailError = !jobViewModelForDetail || jobViewModelForDetail.isValid === false;
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        // 詳細表示のスクロール位置をトップに戻す
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [isJobDetailLoading]);

    /**
     * 多言語化対応のためデザイン側から移植
     */
    const initTagAccordion = () => {
        let $tag = $('.s-job_tag');
        let $tag13over = $('.s-job_tag:nth-child(n+11)');
        let $btn = $('.s-detail_tag_btn');

        if ($tag.length > 12) {
            $tag13over.addClass('is-hide');

            $btn.on('click', function () {
                if ($tag13over.hasClass('is-hide')) {
                    $tag13over.removeClass('is-hide');
                    $btn.addClass('is-open').text(translationsJobItems.show_less).attr('aria-expanded',true);

                } else {
                    $tag13over.addClass('is-hide');
                    $btn.removeClass('is-open').text(translationsJobItems.show_more).attr('aria-expanded',false);
                }
            });
        } else {
            $btn.hide();
        }
    }

    useEffect(() => {
        const singularTxt = translationsJobItems.other_location;
        const pluralTxt = translationsJobItems.other_locations;
        // design entry
        initPreviewSize();
        initTagAccordion();
        initShowMore(singularTxt, pluralTxt);

        // 求人詳細の読み込みが正常に完了したら1PVとして計測
        if (!isFirstRender && !isJobDetailLoading && !isRenderJobDetailError) {
            trackJobDetail(jobViewModelForDetail);
        }
    }, [jobViewModelForDetail]);

    useEffect(() => {
        // ファーストビューの計測
        if (!isRenderJobDetailError) {
            trackJobDetail(jobViewModelForDetail);
        }
    }, []);

    if (isRenderJobDetailError) {
        return (
            <JobDetailError title={jobViewModelForDetail?.title} translations={translationsJobItems} />
        );
    }

    /**
     * 検索結果の勤務地をcookieに保存する
     */
    const saveLocationToCookie = () => {
        if (!jobViewModelForDetail.fromSearchResult) {
            return;
        }

        // safariでcookieに保存した値のスペースが削除されるのでエンコードする
        const locationsFromSearchResult = jobViewModelForDetail.locations.map(location => 
            encodeURIComponent(location.trim())
        );
        const cookieName = 'location_from_search_result';
        const cookieValue = JSON.stringify({
            'uniqueId': jobViewModelForDetail.uniqueId,
            'locationsFromSearchResult': locationsFromSearchResult,
            'fromNeighborhoodSearch': shouldNormalizeLocation,
        });

        saveCookie(cookieName, cookieValue);
    }

    /**
     * 求人詳細へのタブ遷移ボタンクリックのイベントを実行
     */
    const handleLinkDetailPage = () => {
        saveLocationToCookie();

        trackLinkDetailPage(channelName);
    }

    /**
     * リンクコピーiconクリック時のイベントを実行
     */
    const handleCopyLink = () => {
        const jobcubeDetailUrl = jobViewModelForDetail.jobcubeDetailUrl;
        copyTextToClipboard(jobcubeDetailUrl);
        // リンクコピーを計測
        trackLinkCopy(channelName);
    }

    /**
     * show more ボタンのクリックを計測
     * @param e
     */
    const handleTrackShowMore = (e) => {
        const btnText = e.target.textContent;
        // ボタンクリックによりjquery側で"Show More -> Show Less"と書き換わった状態になっている
        if (btnText === translationsJobItems.show_less) {
            trackShowMore(channelName);
        }
    }

    return (
        <>
            <aside className="p-preview_wrap l-c-right u-mt10 s-previewInner s-help_lock">
                <div
                    className="p-preview_content s-preview_content u-mt20"
                    tabIndex="0"
                    aria-label={translationsJobItems.job_details}
                    ref={focusDetailRef}
                >
                    <div className="p-preview_head s-preview_head">
                        <button
                            className="p-preview_focus_btn s-preview_focus_btn"
                            type="button"
                            onClick={() => moveFocusToPanel(jobViewModelForDetail.uniqueId)}
                            onKeyPress={() => moveFocusToPanel(jobViewModelForDetail.uniqueId)}
                        >
                            {translationsJobItems.return_to_search_results}
                        </button>
                        <a
                            href={jobViewModelForDetail.jobcubeDetailUrl}
                            target="_blank"
                            className="p-preview_tab_icon"
                            onClick={handleLinkDetailPage}
                            rel="nofollow"
                        >
                            <img src={mix("webfront/images/icon/tab_gray.svg")} alt={translationsJobItems.open_in_new_tab} width="18" height="18" />
                        </a>

                        <p className="p-detail_head_title p-detail_head_title--preview">{jobViewModelForDetail.title}</p>

                        <div className="p-preview_head_apply">
                            <div className="u-dip_flex">
                                <a href={jobViewModelForDetail.redirectUrl}
                                    onClick={() => trackDetailCv(jobViewModelForDetail, 1, isSaved)}
                                    className="p-preview_apply p-detail_apply c-btn c-btn--primary"
                                    target="_blank"
                                    aria-label={`${translationsJobItems.apply_on_company_site}(${translationsJobItems.open_in_new_tab})`}
                                    rel="nofollow">
                                    {translationsJobItems.apply_on_company_site}<img src={mix("webfront/images/icon/tab_black.svg")} width="15" height="15" alt=""/>
                                </a>
                                <div className="u-dip_flex">
                                    <span className="s-job--saved">
                                        <button
                                            type="button"
                                            className={`p-preview_saved p-detail_saved c-btn s-job_saved${isSaved ? ' is-saved' : ''}`}
                                            aria-label={translations.save_job}
                                            onClick={(e) => handleSaveJob(e, jobViewModelForDetail)}
                                        >
                                            <span className="p-detail_saved--unsaved">
                                                <img
                                                    src={mix("webfront/images/icon/favorite_line_thin.svg")}
                                                    className="p-job_saved_icon"
                                                    width="16"
                                                    height="21"
                                                    alt=""
                                                />
                                            </span>
                                            <span className="p-detail_saved--saved">
                                                <img
                                                    src={mix("webfront/images/icon/favorite.svg")}
                                                    className="p-job_saved_icon"
                                                    width="16"
                                                    height="21"
                                                    alt=""
                                                />
                                            </span>
                                        </button>
                                    </span>
                                    <span>
                                        <button
                                            type="button"
                                            className="p-preview_link p-detail_link c-btn s-alert_trigger"
                                            data-alert-target="copy-alert"
                                            aria-label={translationsJobItems.link_copy}
                                            onClick={() => handleCopyLink()}
                                        >
                                            <span className="p-job_link_icon"></span>
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-preview_body s-preview_body" ref={scrollContainerRef}>
                        <div className="p-preview_content_inner">
                            <ul className="p-detail_summary_wrap">
                                {jobViewModelForDetail.isDisplayCompanyName && (
                                    <li className="p-detail_summary p-detail_summary--preview">
                                        <span className="p-detail_summary_img">
                                            <img
                                                src={mix("webfront/images/icon/company_gray.svg")}
                                                className="p-detail_summary_icon p-detail_summary_icon--preview"
                                                width="19"
                                                height="19"
                                                alt=""
                                            />
                                        </span>
                                        <div className="p-detail_summary_txt">
                                            <p className="p-detail_summary_title">
                                                {translationsJobItems.company}
                                            </p>
                                            <p>
                                                {jobViewModelForDetail.companyName}
                                            </p>
                                        </div>
                                    </li>
                                )}
                                {jobViewModelForDetail.isDisplayLocation && (
                                    <li className="p-detail_summary p-detail_summary--preview u-pos_r">
                                        <span className="p-detail_summary_img">
                                            <img
                                                src={mix("webfront/images/icon/location_gray.svg")}
                                                className="p-detail_summary_icon p-detail_summary_icon--preview"
                                                width="19"
                                                height="19"
                                                alt=""
                                            />
                                        </span>
                                        <div className="p-detail_summary_txt">
                                            <p className="p-detail_summary_title">
                                                {translationsJobItems.location}
                                            </p>
                                            <div className="c-more s-more_list_wrap">
                                                {jobViewModelForDetail?.locations?.map((location, index) => (
                                                    <span key={index} className="c-more_list s-more_list">{location}</span>
                                                ))}
                                                {"\u00A0"}
                                                <button type="button" className="c-more_btn s-more_text">
                                                    + <span className="s-more_num"></span> <span className="s-more_loc">{translationsJobItems.other_locations}</span>
                                                </button>
                                            </div>
                                            <div className="p-detail_summary_baloon c-more_baloon s-more_baloon">
                                                <span className="u-pos_r s-more_baloon_triangle_pos">
                                                    <span className="c-more_baloon_triangle c-more_baloon_triangle--bottom s-more_baloon_triangle"></span>
                                                    <span className="c-more_baloon_triangle c-more_baloon_triangle--bottom s-more_baloon_triangle"></span>
                                                </span>

                                                <button className="c-more_baloon_close s-more_baloon_close">
                                                    <img src={mix("webfront/images/icon/cross_darkgray.svg")} width="13" height="13" alt=""/>
                                                </button>

                                                <ul className="c-more_baloon_list_wrap s-more_baloon_list_wrap">
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                )}
                                {jobViewModelForDetail.isDisplayJobType && (
                                    <li className="p-detail_summary p-detail_summary--preview">
                                        <span className="p-detail_summary_img">
                                            <img
                                                src={mix("webfront/images/icon/jobtype_gray.svg")}
                                                className="p-detail_summary_icon p-detail_summary_icon--preview"
                                                width="19"
                                                height="19"
                                                alt=""
                                            />
                                        </span>
                                        <div className="p-detail_summary_txt">
                                            <p className="p-detail_summary_title">
                                                {translationsJobItems.job_type}
                                            </p>
                                            <p>
                                                {jobViewModelForDetail.jobType}
                                            </p>
                                        </div>
                                    </li>
                                )}
                                {jobViewModelForDetail.isDisplayStation && (
                                    <li className="p-detail_summary p-detail_summary--preview">
                                        <span className="p-detail_summary_img">
                                            <img
                                                src={mix("webfront/images/icon/station_gray.svg")}
                                                className="p-detail_summary_icon p-detail_summary_icon--preview"
                                                width="19"
                                                height="19"
                                                alt=""
                                            />
                                        </span>
                                        <div className="p-detail_summary_txt">
                                            <p className="p-detail_summary_title">
                                                {translationsJobItems.station}
                                            </p>
                                            <p>
                                                {jobViewModelForDetail.stationName}
                                                <span className="p-detail_summary_train">
                                                    {Object.values(jobViewModelForDetail.trainList).map((train, index) => (
                                                        <img
                                                            key={index}
                                                            src={mix(`webfront/images/train/train_${train.id}.svg`)}
                                                            className="p-detail_summary_icon p-detail_summary_icon--preview"
                                                            width="17"
                                                            height="17"
                                                            alt={train.name}
                                                        />
                                                    ))}
                                                </span>
                                            </p>
                                        </div>
                                    </li>
                                )}
                                {jobViewModelForDetail.isDisplaySalary && (
                                    <li className="p-detail_summary p-detail_summary--preview">
                                        <span className="p-detail_summary_img">
                                            <img
                                                src={mix("webfront/images/icon/bills_gray.svg")}
                                                className="p-detail_summary_icon p-detail_summary_icon--preview"
                                                width="19"
                                                height="19"
                                                alt=""
                                            />
                                        </span>
                                        <div className="p-detail_summary_txt">
                                            <p className="p-detail_summary_title">
                                                {translationsJobItems.salary}
                                            </p>
                                            <p>
                                                {jobViewModelForDetail.salary}
                                            </p>
                                        </div>
                                    </li>
                                )}
                            </ul>
                            {jobViewModelForDetail.isDisplayFeatures && (
                                <>
                                    <div className="p-detail_tag p-detail_tag--preview s-detail_tag">
                                        {jobViewModelForDetail.featureViewModel.keywordMatchFeatures && (
                                            <>
                                                {jobViewModelForDetail.featureViewModel.keywordMatchFeatures.map((keywordMatchFeature, index) => (
                                                    <p key={index} className="p-job_tag s-job_tag">
                                                        <img src={mix("webfront/images/icon/check_circle.svg")}
                                                            className="p-job_tag_image"
                                                             width="14" height="14" alt={translationsJobItems.matching}/>
                                                        {keywordMatchFeature}
                                                    </p>
                                                ))}
                                            </>
                                        )}
                                        {jobViewModelForDetail.featureViewModel.remoteFeatures.map((remoteFeature, index) => (
                                            <p key={index} className="p-job_tag s-job_tag">
                                                <img src={mix("webfront/images/icon/wifi_bluegray.svg")}
                                                    className="p-job_tag_image"
                                                     width="14" height="14" alt=""/>
                                                {remoteFeature}
                                            </p>
                                        ))}
                                        {jobViewModelForDetail.featureViewModel.bonusFeatures.map((remoteFeature, index) => (
                                            <p key={index} className="p-job_tag s-job_tag">
                                                <img src={mix("webfront/images/icon/bonus_bluegray.svg")}
                                                    className="p-job_tag_image"
                                                     width="14" height="14" alt=""/>
                                                {remoteFeature}
                                            </p>
                                        ))}
                                        {jobViewModelForDetail.featureViewModel.immediatelyHiringFeatures.map((immediatelyHiringFeature, index) => (
                                            <p key={index} className="p-job_tag s-job_tag">
                                                <img src={mix("webfront/images/icon/thunder.svg")}
                                                    className="p-job_tag_image"
                                                     width="9" height="15" alt=""/>
                                                {immediatelyHiringFeature}
                                            </p>
                                        ))}

                                        {jobViewModelForDetail.featureViewModel.normalAllFeatures.map((feature, index) => (
                                            <p key={index} className="p-job_tag s-job_tag">{feature}</p>
                                        ))}
                                    </div>
                                    <button className="p-detail_tag_btn s-detail_tag_btn" aria-expanded="false" onClick={handleTrackShowMore}>
                                        {translationsJobItems.show_more}
                                    </button>
                                </>
                            )}
                        </div>

                        {
                            isJobDetailLoading
                                ? <LoadingContent />
                                : <LoadedContent
                                    jobViewModelForDetail={jobViewModelForDetail}
                                    isSaved={isSaved}
                                    handleSaveJob={handleSaveJob}
                                    moveFocusToPanel={moveFocusToPanel}
                                    translationsJobItems={translationsJobItems}
                                />
                        }
                    </div>
                </div>
            </aside>

            {/* Modal */}
            <div className="p-modal_bg s-modal_bg"></div>
            <div className="p-help_balloon s-modal s-help_balloon s-modal--detail" data-modal="detail" role="dialog" aria-modal="true">
                <div className="p-help_balloon_inner">
                    <div className="p-help_balloon_header">
                        <div className="p-modal_close s-modal_close">
                            <button className="p-modal_close_in s-modal_focus" aria-label={translations.close}>
                                <img src={mix("webfront/images/icon/cross.svg")} width="21" height="20" alt=""/>
                            </button>
                        </div>
                    </div>
                    <div className="p-help_balloon_body">
                        <div className="p-help_balloon_txt">
                            <p className="p-detail_lines">{translationsJobItems.this_job_posting_was_obtained.replace(':source_name', jobViewModelForDetail.sourceName)}</p>
                            <p className="p-detail_lines">{translationsJobItems.jobcube_make_no_representations}</p>
                            <p>{translationsJobItems.inquire_with_the_poster}</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const LoadingContent = memo(() => {
    return (
        <div className="p-preview_content_inner">
            <div className="p-detail_lines_wrap">
                <h2 className="p-detail_title p-detail_title--1"><span
                    className="c-skeleton c-skeleton--w40"></span></h2>
                <p className="p-detail_lines">
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton c-skeleton--w60"></span>
                </p>
            </div>
            <div className="p-detail_lines_wrap">
                <h3 className="p-detail_title p-detail_title--2"><span
                    className="c-skeleton c-skeleton--w40"></span></h3>
                <ul className="p-detail_list_wrap">
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span className="c-skeleton"></span></li>
                    <li className="p-detail_list"><span
                        className="c-skeleton c-skeleton--w80"></span></li>
                </ul>
            </div>
            <div className="p-detail_lines_wrap">
                <h2 className="p-detail_title p-detail_title--1"><span
                    className="c-skeleton c-skeleton--w40"></span></h2>
                <p className="p-detail_lines">
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton"></span>
                    <span className="c-skeleton c-skeleton--w60"></span>
                </p>
            </div>
        </div>
    );
});

const LoadedContent = ({ jobViewModelForDetail, isSaved, handleSaveJob, moveFocusToPanel, translationsJobItems }) => {
    const { translations } = usePage().props;

    return (
        <>
            {jobViewModelForDetail.upperPanelSectionViewModel.isDisplay && (
                <div className="p-preview_content_inner">
                    {jobViewModelForDetail.upperPanelSectionViewModel.panelContentViewModels.map((panelContentViewModel, index) => (
                        panelContentViewModel.isDisplay && (
                            <div key={index} className="p-detail_lines_wrap">
                                <span dangerouslySetInnerHTML={{ __html: panelContentViewModel.titleHtml }} />
                                <span dangerouslySetInnerHTML={{ __html: panelContentViewModel.valueHtml }} />
                            </div>
                        )
                    ))}
                </div>
            )}

            {jobViewModelForDetail.tableSectionViewModel.isDisplay && (
                <div className="p-preview_content_inner">
                    {jobViewModelForDetail.tableSectionViewModel.tablePartViewModels.map((tablePartViewModel, index) => (
                        tablePartViewModel.isDisplay && (
                            <dl key={index} className="p-preview_detail_table">
                                <dt className="p-preview_table_icon">
                                    <span dangerouslySetInnerHTML={{ __html: tablePartViewModel.iconHtml }} />
                                </dt>
                                <dd className="p-detail_table_inner">
                                    <div className="p-detail_table_content">
                                        {tablePartViewModel.tableContentViewModels.map((tableContentViewModel, index) => (
                                            <Fragment key={index}>
                                                <span dangerouslySetInnerHTML={{ __html: tableContentViewModel.titleHtml }} />
                                                <span dangerouslySetInnerHTML={{ __html: tableContentViewModel.valueHtml }} />
                                            </Fragment>
                                        ))}
                                    </div>

                                    {tablePartViewModel.isDisplayMap && (
                                        <GoogleMap
                                            lat={tablePartViewModel.lat}
                                            lng={tablePartViewModel.lng}
                                        />
                                    )}
                                </dd>
                            </dl>
                        )
                    ))}
                </div>
            )}

            {jobViewModelForDetail.bottomSectionViewModel.isDisplay && (
                <div className="p-preview_content_inner">
                    {jobViewModelForDetail.bottomSectionViewModel.panelContentViewModels.map((panelContentViewModel, index) => (
                        panelContentViewModel.isDisplay && (
                            <div key={index} className="p-detail_lines_wrap">
                                <span dangerouslySetInnerHTML={{ __html: panelContentViewModel.titleHtml }} />
                                <span dangerouslySetInnerHTML={{ __html: panelContentViewModel.valueHtml }} />
                            </div>
                        )
                    ))}
                </div>
            )}

            <div className="p-preview_foot_wrap">
                <div className="p-preview_foot_apply u-dip_flex">
                    <a
                        href={jobViewModelForDetail.redirectUrl}
                        onClick={() => trackDetailCv(jobViewModelForDetail, 2, isSaved)}
                        className="p-preview_apply p-detail_apply c-btn c-btn--primary"
                        target="_blank"
                        aria-label={`${translationsJobItems.apply_on_company_site}(${translationsJobItems.open_in_new_tab})`}
                        rel="nofollow"
                    >{translationsJobItems.apply_on_company_site}<img src={mix("webfront/images/icon/tab_black.svg")} width="15" height="15" alt=""/>
                    </a>
                    <span className="s-job--saved">
                        <button
                            type="button"
                            className={`p-preview_saved p-detail_saved p-detail_saved--text c-btn s-job_saved ${isSaved ? 'is-saved' : ''}`}
                            aria-label={translations.save_job}
                            onClick={(e) => handleSaveJob(e, jobViewModelForDetail)}
                        >
                            <span className="p-detail_saved--unsaved">
                                <img
                                    src={mix("webfront/images/icon/favorite.svg")}
                                    className="p-job_saved_icon p-job_saved_icon--saved"
                                    width="16"
                                    height="21"
                                    alt=""
                                />
                                <img
                                    src={mix("webfront/images/icon/favorite_line_thin.svg")}
                                    className="p-job_saved_icon p-job_saved_icon--unsaved"
                                    width="16"
                                    height="21"
                                    alt=""
                                />
                                <p className="p-detail_saved_txt">{translationsJobItems.save_this_job}</p>
                            </span>
                            <span className="p-detail_saved--saved">
                                <img
                                    src={mix("webfront/images/icon/favorite.svg")}
                                    className="p-job_saved_icon p-job_saved_icon--saved"
                                    width="16"
                                    height="21"
                                    alt=""
                                />
                                <img
                                    src={mix("webfront/images/icon/favorite_line_thin.svg")}
                                    className="p-job_saved_icon p-job_saved_icon--unsaved"
                                    width="16"
                                    height="21"
                                    alt=""
                                />
                                <p className="p-detail_saved_txt">{translations.saved}</p>
                            </span>
                        </button>
                    </span>
                </div>
                <div className="p-help_btn_wrap s-help_btn_wrap s-help--detail u-mt20">
                    <button type="button" className="p-help_btn s-help">{translationsJobItems.about_this_page}</button>
                </div>
                <button
                    className="p-preview_focus_btn s-preview_focus_btn"
                    onClick={() => moveFocusToPanel(jobViewModelForDetail.uniqueId)}
                    onKeyPress={() => moveFocusToPanel(jobViewModelForDetail.uniqueId)}
                >
                    {translationsJobItems.return_to_search_results}
                </button>
            </div>
        </>
    );
}

export default JobDetail;
